import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Styled replacement for window.confirm / window.prompt.
// Wrap the app in <ConfirmProvider> once, then:
//   const confirm = useConfirm();
//   const ok = await confirm({ title: 'Delete vendor', message: 'This cannot be undone.', confirmLabel: 'Delete', danger: true });
// With an input (prompt-style), resolves to the entered string, or null on cancel:
//   const reason = await confirm({ title: 'Reject expense', input: { label: 'Reason', placeholder: 'Optional' }, confirmLabel: 'Reject', danger: true });

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const resolveRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const inputRef = useRef(null);
  const previousFocusRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      previousFocusRef.current = document.activeElement;
      resolveRef.current = resolve;
      setInputValue(options?.input?.defaultValue || '');
      setDialog({
        title: options?.title || 'Are you sure?',
        message: options?.message || '',
        confirmLabel: options?.confirmLabel || 'Confirm',
        cancelLabel: options?.cancelLabel || 'Cancel',
        danger: Boolean(options?.danger),
        input: options?.input || null,
      });
    });
  }, []);

  const close = useCallback((result) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setDialog(null);
    const prev = previousFocusRef.current;
    if (prev && typeof prev.focus === 'function') prev.focus();
  }, []);

  const handleCancel = useCallback(() => {
    close(dialog?.input ? null : false);
  }, [close, dialog]);

  const handleConfirm = useCallback(() => {
    close(dialog?.input ? inputValue : true);
  }, [close, dialog, inputValue]);

  useEffect(() => {
    if (!dialog) return;
    const el = dialog.input ? inputRef.current : confirmBtnRef.current;
    if (el) el.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialog, handleCancel]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="confirm-dialog-overlay" onClick={handleCancel}>
          <div
            className="confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={dialog.title}
            onClick={e => e.stopPropagation()}
          >
            <h3>{dialog.title}</h3>
            {dialog.message && (
              <p style={{
                margin: '0 0 20px',
                fontFamily: 'var(--font-sans)', fontSize: '14px',
                color: 'var(--body-text)', lineHeight: 1.55,
              }}>
                {dialog.message}
              </p>
            )}
            {dialog.input && (
              <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
                <span style={{
                  fontFamily: 'var(--font-sans)', fontSize: '12px', fontWeight: 500,
                  color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1.2px',
                  marginBottom: '8px',
                }}>
                  {dialog.input.label}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  className="form-input"
                  value={inputValue}
                  placeholder={dialog.input.placeholder || ''}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
                  style={{ marginTop: 0 }}
                />
              </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                className={dialog.danger ? 'btn-danger-solid' : 'btn-primary'}
                onClick={handleConfirm}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Provider missing; fall back to the native dialog rather than crashing.
    return (options) => Promise.resolve(window.confirm(options?.message || options?.title || 'Are you sure?'));
  }
  return ctx;
};
